/* eslint-disable no-restricted-syntax */
/* eslint-disable prefer-promise-reject-errors */
/* eslint-disable no-async-promise-executor */
import { Injectable } from '@angular/core';
import { ModalController, ToastController } from '@ionic/angular';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { StarPrinterService } from '../star-micronics/star-printer.service';
import { OrderService } from '../order/order.service';
import { EmulatedPrinterService } from './emulated-printer.service';
import {
  PrintCommands,
  ReceiptGeneratorService,
  ReceiptMemberInfo,
  ReceiptOptions,
} from './receipt-generator.service';
import { PrinterRole, PrinterPaperWidth } from '../../enums';
import { PosCashDrawer, PosPrinter, Printer, IShift, Venue } from '../../models';
import { ReciprocalChargeInfo } from '../../helpers/reciprocal.helper';
import { OrderData } from '../../interfaces';
import { SelectPrinterModalComponent } from '../../shared/select-printer-modal/select-printer-modal.component';
import { selectPrinters, selectShift, selectVenue } from '../../state';

export const EMULATED_PRINTER_MODEL = 'Emulated';
export const EMULATED_PRINTER_PORT = 'emulated';

interface PrintOptions {
  printers: PosPrinter[]; // Array of printers setup
  order?: OrderData; // Object representing the order
  transaction?: any; // Charge object which is needed for receipts if post payment
  isEdit?: boolean; // Whether to only print non-saved (i.e. newly added) items
  shift?: any; // The shift to print
  summary?: any; // The summary for the shift
  reportData?: any; // The summary for the shift
  netTenderSummary?: Record<string, { amount: number; name: string }>[]; // The summary for the shift
  quantity?: number; // The number of chits to print
  member?: ReceiptMemberInfo;
  memberChargesEnabled?: boolean;
  reciprocal?: ReciprocalChargeInfo;
}

interface PrintJobOptions {
  port: string;
  emulation: string;
  paperWidth?: number;
}

export interface ReceiptPrintJobOptions {
  orderId: string;
  order: OrderData;
  printers?: PosPrinter[];
  transaction?: any;
  transactionId?: string;
  printerId?: string;
  quantity?: number;
  member?: ReceiptMemberInfo;
  memberChargesEnabled?: boolean;
  reciprocal?: ReciprocalChargeInfo;
}

export interface FulfillmentPrintJobOptions {
  orderId: string;
  order: OrderData;
  printers?: PosPrinter[];
  printerId?: string;
  quantity?: number;
  isEdit?: boolean;
}

export interface ShiftPrintJobOptions {
  printers?: PosPrinter[];
  shift: any;
  summary: any;
  reportData: any;
  netTenderSummary: Record<string, { amount: number; name: string }>[];
}

@Injectable({
  providedIn: 'root',
})
export class PrintService {
  public cloudReceiptPrinters: Printer[] = [];

  public cloudFulfillmentPrinters: Printer[] = [];

  private shift: IShift = null;

  private cloudPrinters$: Observable<Printer[]> = this.store.pipe(select(selectPrinters));

  private venue$: Observable<Venue> = this.store.pipe(select(selectVenue));

  private shift$: Observable<IShift> = this.store.pipe(select(selectShift));

  private receiptConfig: ReceiptOptions;

  constructor(
    private store: Store,
    private _toastCtrl: ToastController,
    private starPrinterService: StarPrinterService,
    private emulatedPrinterService: EmulatedPrinterService,
    private receiptGenerator: ReceiptGeneratorService,
    private orderService: OrderService,
    private modalCtrl: ModalController
  ) {
    this.watchCloudPrinters();
  }

  public static isEmulatedPrinter(printer?: PosPrinter): boolean {
    return (
      printer?.model === EMULATED_PRINTER_MODEL || printer?.port === EMULATED_PRINTER_PORT
    );
  }

  public static hasPrinterForRole(printers: PosPrinter[] = [], role: PrinterRole): boolean {
    return printers.some(printer => printer.roles?.includes(role));
  }

  public static createEmulatedPrinter(): PosPrinter {
    return {
      model: EMULATED_PRINTER_MODEL,
      port: EMULATED_PRINTER_PORT,
      cashdrawerAttached: false,
      paperWidth: PrinterPaperWidth.MM48,
      roles: [PrinterRole.RECEIPT, PrinterRole.FULFILLMENT, PrinterRole.SHIFT],
      stations: [],
    };
  }

  public canPrintLocally(role: PrinterRole, printers: PosPrinter[] = []): boolean {
    return PrintService.hasPrinterForRole(printers, role);
  }

  private getLocalPrinterForRole(
    printers: PosPrinter[],
    role: PrinterRole
  ): PosPrinter | undefined {
    return printers.find(printer => printer.roles?.includes(role));
  }

  private printedWithEmulated(printers: PosPrinter[], role: PrinterRole): boolean {
    return PrintService.isEmulatedPrinter(this.getLocalPrinterForRole(printers, role));
  }

  private resolveTransaction(order: OrderData, transaction?: any, transactionId?: string): any {
    if (transaction) {
      return transaction;
    }
    if (!transactionId) {
      return undefined;
    }
    if (typeof transactionId === 'object') {
      return transactionId;
    }
    return order?.transactions?.find((tx: any) => tx._id === transactionId);
  }

  public async executeReceiptPrint(options: ReceiptPrintJobOptions): Promise<any | null> {
    const printers = options.printers || [];
    const transaction = this.resolveTransaction(
      options.order,
      options.transaction,
      options.transactionId
    );
    if (this.canPrintLocally(PrinterRole.RECEIPT, printers)) {
      await this.printTypes([PrinterRole.RECEIPT], {
        printers,
        order: options.order,
        transaction,
        quantity: options.quantity ?? 1,
        member: options.member,
        memberChargesEnabled: options.memberChargesEnabled,
        reciprocal: options.reciprocal,
      });
      return {
        success: true,
        data: { message: 'Printed receipt' },
        emulated: this.printedWithEmulated(printers, PrinterRole.RECEIPT),
      };
    }

    const printerId = options.printerId ?? (await this.getCloudPrinterId(PrinterRole.RECEIPT));
    if (!printerId) {
      return null;
    }
    return this.orderService
      .printCloudReceipt(
        options.orderId,
        transaction?._id ?? (options.transactionId as string | undefined),
        printerId,
        options.quantity ?? 1
      )
      .toPromise();
  }

  public async executeFulfillmentPrint(
    options: FulfillmentPrintJobOptions
  ): Promise<any | null> {
    const printers = options.printers || [];
    if (this.canPrintLocally(PrinterRole.FULFILLMENT, printers)) {
      await this.printTypes([PrinterRole.FULFILLMENT], {
        printers,
        order: options.order,
        isEdit: options.isEdit,
        quantity: options.quantity ?? 1,
      });
      return {
        success: true,
        data: { message: 'Printed fulfillment ticket' },
        emulated: this.printedWithEmulated(printers, PrinterRole.FULFILLMENT),
      };
    }

    const printerId = options.printerId ?? (await this.getCloudPrinterId(PrinterRole.FULFILLMENT));
    if (!printerId) {
      return null;
    }
    return this.orderService
      .printCloudFulfillment(options.orderId, printerId, options.quantity ?? 1)
      .toPromise();
  }

  public async executeShiftPrint(options: ShiftPrintJobOptions): Promise<any | null> {
    const printers = options.printers || [];
    if (!this.canPrintLocally(PrinterRole.SHIFT, printers)) {
      return null;
    }
    await this.printTypes([PrinterRole.SHIFT], {
      printers,
      shift: options.shift,
      summary: options.summary,
      reportData: options.reportData,
      netTenderSummary: options.netTenderSummary,
    });
    return {
      success: true,
      data: { message: 'Printed shift report' },
      emulated: this.printedWithEmulated(printers, PrinterRole.SHIFT),
    };
  }

  private watchCloudPrinters() {
    this.cloudPrinters$.subscribe(printers => {
      this.cloudReceiptPrinters = printers?.filter(p =>
        p.data.printerRole.includes(PrinterRole.RECEIPT)
      );
      this.cloudFulfillmentPrinters = printers?.filter(p =>
        p.data.printerRole.includes(PrinterRole.FULFILLMENT)
      );
    });
    this.venue$.subscribe(venue => {
      this.receiptConfig = venue?.data.guestReceiptConfig;
    });
    this.shift$.subscribe(shift => {
      this.shift = shift;
    });
  }

  public async managePrinting(types: PrinterRole[], options: PrintOptions) {
    try {
      const results = await this.printTypes(types, options);
      console.log('manage printing results');
      console.log(results);
    } catch (e) {
      console.log('manage printing error');
      console.log(e);
      const toast = await this._toastCtrl.create({
        message: e.message,
        color: 'warning',
        position: 'top',
        duration: 3000,
      });
      await toast.present();
    }
  }

  public async printTypes(types: PrinterRole[], options: PrintOptions): Promise<any> {
    const { quantity = 1 } = options;
    return new Promise(async (resolve, reject) => {
      try {
        const results = [];
        for await (const type of types) {
          switch (type) {
            case PrinterRole.RECEIPT: {
              const receiptResult = await this.receipt(
                options.printers,
                this.receiptConfig,
                options.order,
                options.transaction,
                options.member,
                options.memberChargesEnabled,
                options.reciprocal
              );
              for (let i = 0; i < quantity; i++) {
                results.push(receiptResult);
              }
              break;
            }
            case PrinterRole.FULFILLMENT: {
              const kitchenResult = await this.printKitchen(
                options.printers,
                options.order,
                options.isEdit
              );
              for (let i = 0; i < quantity; i++) {
                results.push(kitchenResult);
              }
              break;
            }
            case PrinterRole.SHIFT: {
              const shiftResult = await this.printShift(options);
              for (let i = 0; i < quantity; i++) {
                results.push(shiftResult);
              }
            }
          }
        }
        resolve(results);
      } catch (e) {
        reject(e);
      }
    });
  }

  private static getPaperWidthChars(printer: PosPrinter): number {
    if (printer.paperWidth) {
      return printer.paperWidth;
    }
    switch (printer.model) {
      case 'SM-L200':
      case 'mPOP':
        return PrinterPaperWidth.MM32;
      default:
        return PrinterPaperWidth.MM48;
    }
  }

  private static buildPrintJobOptions(printer: PosPrinter): PrintJobOptions {
    return {
      port: printer.port,
      emulation: PrintService.getEmulationFromPrinterModel(printer.model),
      paperWidth: PrintService.getPaperWidthChars(printer),
    };
  }

  private static getEmulationFromPrinterModel(model: string): string {
    if (model === EMULATED_PRINTER_MODEL) {
      return '';
    }
    switch (model) {
      case 'mPOP':
        return 'StarPRNT';
      case 'FVP10':
        return 'StarLine';
      case 'TSP100':
        return 'StarGraphic';
      case 'TSP650II':
        return 'StarLine';
      case 'TSP700II':
        return 'StarLine';
      case 'TSP800II':
        return 'StarLine';
      case 'SP700':
        return 'StarDotImpact';
      case 'SM-S210i':
        return 'EscPosMobile';
      case 'SM-S220i':
        return 'EscPosMobile';
      case 'SM-S230i':
        return 'EscPosMobile';
      case 'SM-T300i':
        return 'EscPosMobile';
      case 'SM-T300':
        return 'EscPosMobile';
      case 'SM-T400i':
        return 'EscPosMobile';
      case 'SM-L200':
        return 'StarPRNT';
      case 'SM-L300':
        return 'StarPRNT';
    }
    return 'StarPRNT';
  }

  public async testPrint(printer: PosPrinter) {
    return new Promise(async (resolve, reject) => {
      try {
        const result = await this.print(
          PrintService.buildPrintJobOptions(printer),
          this.receiptGenerator.getTestReceipt(printer)
        );
        resolve(result);
      } catch (e) {
        reject(e);
      }
    });
  }

  public async receipt(
    printers: PosPrinter[],
    receiptConfig: ReceiptOptions,
    order: OrderData,
    transaction?: any,
    member?: ReceiptMemberInfo,
    memberChargesEnabled?: boolean,
    reciprocal?: ReciprocalChargeInfo
  ): Promise<any> {
    return new Promise(async (resolve, reject) => {
      const receiptPrinter = printers.find(x => x.roles.includes(PrinterRole.RECEIPT));
      if (receiptPrinter) {
        try {
          const receipt = this.receiptGenerator.receipt(
            receiptPrinter,
            receiptConfig,
            order,
            this.shift?.openingBy,
            transaction,
            member,
            memberChargesEnabled,
            reciprocal
          );
          const result = await this.print(
            PrintService.buildPrintJobOptions(receiptPrinter),
            receipt
          );
          resolve(result);
        } catch (e) {
          reject(e);
        }
      } else {
        resolve({ message: 'No receipt printers setup' });
      }
    });
  }

  public async printKitchen(
    printers: PosPrinter[],
    order: OrderData,
    isEdit?: boolean
  ): Promise<any> {
    return new Promise(async resolve => {
      const kitchenPrinters = printers.filter(x => x.roles.includes(PrinterRole.FULFILLMENT));
      const jobs: {
        printer: PosPrinter;
        station?: string;
        commands: PrintCommands;
      }[] = [];
      kitchenPrinters.forEach(printer => {
        if (printer.stations && printer.stations.length) {
          printer.stations.forEach(station => {
            const commands = this.receiptGenerator.kitchenReceipt(
              printer,
              order,
              station,
              isEdit,
              this.shift?.openingBy
            );
            if (!commands.length) {
              return;
            }
            jobs.push({
              printer,
              station,
              commands,
            });
          });
        } else {
          const commands = this.receiptGenerator.kitchenReceipt(
            printer,
            order,
            null,
            isEdit,
            this.shift?.openingBy
          );
          if (!commands.length) {
            return;
          }
          jobs.push({
            printer,
            commands,
          });
        }
      });
      if (jobs.length) {
        const results = [];
        for await (const printJob of jobs) {
          const printJobResult = await this.print(
            PrintService.buildPrintJobOptions(printJob.printer),
            printJob.commands
          );
          results.push(printJobResult);
        }
        resolve(results);
      } else {
        resolve({ message: 'No fulfillment printers setup' });
      }
    });
  }

  public async printShift(options: PrintOptions): Promise<any> {
    return new Promise(async (resolve, reject) => {
      const { printers = [], shift, reportData, netTenderSummary } = options;
      const shiftPrinter = printers.find(printer => printer.roles.includes(PrinterRole.SHIFT));
      if (shiftPrinter) {
        try {
          const commands = this.receiptGenerator.shiftReport(shiftPrinter, shift, {
            ...reportData,
            netTenderSummary,
          });
          const result = await this.print(
            PrintService.buildPrintJobOptions(shiftPrinter),
            commands
          );
          resolve(result);
        } catch (e) {
          reject(e);
        }
      } else {
        reject({ message: 'No shift printer is setup' });
      }
    });
  }

  private async print(options: PrintJobOptions, commands: PrintCommands): Promise<any> {
    return new Promise(async resolve => {
      if (commands.length) {
        try {
          const useEmulatedPrinter = options.port === EMULATED_PRINTER_PORT;
          const result = useEmulatedPrinter
            ? await this.emulatedPrinterService.print(commands, options)
            : await this.starPrinterService.print(commands, options);
          if (!useEmulatedPrinter) {
            await this.starPrinterService.disconnect();
          }
          resolve(result);
        } catch (e) {
          const message = e?.message || e?.toString?.() || 'Printer error';
          console.log('print error', e);
          resolve({ success: false, message });
        }
      } else {
        resolve({ message: 'No receipt to be printed' });
      }
    });
  }

  public async openCashDrawer(cashDrawer: PosCashDrawer): Promise<any> {
    return new Promise(async resolve => {
      try {
        const result = await this.starPrinterService.openCashDrawer({
          port: cashDrawer.port,
          emulation: '',
        });
        await this.starPrinterService.disconnect();
        resolve(result);
      } catch (e) {
        const message = e?.message || e?.toString?.() || 'Cash drawer error';
        console.log('openCashDrawer error', e);
        resolve({ success: false, message });
      }
    });
  }

  public async isConnected(printer: PosPrinter): Promise<boolean> {
    if (PrintService.isEmulatedPrinter(printer)) {
      return true;
    }
    return this.starPrinterService.isConnected({
      port: printer.port,
      emulation: PrintService.getEmulationFromPrinterModel(printer.model),
    });
  }

  public async getCloudPrinterId(type: PrinterRole): Promise<string | null> {
    const cloudPrinters =
      type === PrinterRole.RECEIPT ? this.cloudReceiptPrinters : this.cloudFulfillmentPrinters;
    if (!cloudPrinters.length) {
      return null;
    }
    if (cloudPrinters.length === 1) {
      return cloudPrinters[0].id;
    }
    const modal = await this.modalCtrl.create({
      component: SelectPrinterModalComponent,
      componentProps: {
        title: 'Select Printer',
        printers: cloudPrinters,
      },
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    if (data?.printer) {
      return data.printer.id;
    }
    return null;
  }
}
